import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  Fade,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

const images = [
  {
    src: '/images/carousel/kids-at-school.jpg',
    alt: 'Diverse students in a classroom',
    title: 'website.header.slides.0.title',
    description: 'website.header.slides.0.description',
    subtitle: 'website.header.slides.0.subtitle',
  },
  {
    src: '/images/carousel/tech-at-school.jpg',
    alt: 'Student using laptop',
    title: 'website.header.slides.1.title',
    description: 'website.header.slides.1.description',
    subtitle: 'website.header.slides.1.subtitle',
  },
  {
    src: '/images/carousel/meeting-tech.jpeg',
    alt: 'Team meeting and mentoring',
    title: 'website.header.slides.2.title',
    description: 'website.header.slides.2.description',
    subtitle: 'website.header.slides.2.subtitle',
  },
];

const Carousel = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 7000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      id="header"
      position="relative"
      mt={10}
      height={{ base: '60vh', md: '70vh' }}
      overflow="hidden"
      color="white"
      textAlign="center"
    >
      {images.map((image, index) => (
        <Fade
          key={image.src}
          in={index === currentIndex}
          unmountOnExit
          transition={{ enter: { duration: 1.2 }, exit: { duration: 0.8 } }}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            pointerEvents: index === currentIndex ? 'auto' : 'none',
          }}
        >
          <Box
            backgroundImage={`url(${image.src})`}
            backgroundSize="cover"
            backgroundPosition="center"
            w="100%"
            h="100%"
            position="absolute"
            top="0"
            left="0"
            zIndex="-1"
            _after={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bg: 'rgba(0, 0, 0, 0.2)',
            }}
          />
        </Fade>
      ))}

      <Box position="relative" pt={{ base: '5rem', md: '8rem' }} px={4}>
        <Heading
          as="h1"
          mt={20}
          fontSize={{ base: '3xl', md: '5xl' }}
          textShadow="2px 2px 6px rgba(0, 0, 0, 0.8)"
        >
          {t(images[currentIndex].title)}
        </Heading>

        <Heading
          as="h3"
          mt={10}
          fontSize={{ base: 'lg', md: '2xl' }}
          textShadow="2px 2px 6px rgba(0, 0, 0, 0.8)"
        >
          {t(images[currentIndex].description)}
        </Heading>

        <Text
          mt={2}
          fontSize={{ base: 'md', md: 'xl' }}
          fontStyle="italic"
          textShadow="2px 2px 6px rgba(0, 0, 0, 0.8)"
          maxW="700px"
          mx="auto"
        >
          {t(images[currentIndex].subtitle)}
        </Text>
      </Box>

      <Flex
        justify="center"
        position="absolute"
        bottom="4"
        left="0"
        right="0"
        zIndex="2"
      >
        {images.map((_, index) => (
          <Box
            key={index}
            mx={1}
            my={index === currentIndex ? 0 : '2px'}
            w={index === currentIndex ? 3 : 2}
            h={index === currentIndex ? 3 : 2}
            borderRadius="full"
            bg={index === currentIndex ? 'whiteAlpha.900' : 'whiteAlpha.500'}
            cursor="pointer"
            transition="all 0.3s"
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </Flex>
    </Box>
  );
};

export default Carousel;
